import { createEventQueue } from '@pexip/peer-connection';
import { createAsyncQueue, isEmpty } from '@pexip/utils';
import { TURN_ICE_RESTART_DELAY_MS } from './constants';
import { logger } from './logger';
import { isMainConfig, isPresentationRevoked, isPresoVideo, isVideoConfig, toIceCandidate, } from './utils';
// -----------------------------------------------------------------------------
// Packet Stagnation Detection
// -----------------------------------------------------------------------------
const createPacketStagnationHandler = ({ isDirectMedia, callSignals, addOnceApiCallback, restartIce, getPeer, resetStats, }) => {
    let videoPacketsReceived = 0;
    return (stats) => {
        if (!stats) {
            return;
        }
        const update = stats.packetsTransmitted;
        if (update < videoPacketsReceived) {
            // likely a stale update from just recovered connection
            return;
        }
        if (isDirectMedia) {
            // FIXME: When the remote peer mute the camera, it is likely a hard-mute,
            // there will be no packet transmitted. We should use another way to detect packet stagnation.
            // Ref https://github.com/pexip/aquila/issues/3066
            return;
        }
        if (videoPacketsReceived !== 0 && videoPacketsReceived === update) {
            logger.info('Triggering ICE restart due to packet stagnation');
            const resumeStatsUpdates = resetStats();
            addOnceApiCallback('newCandidate', () => {
                /**
                 * Stats could start flowing before new packets.
                 * Increment the received packets in order to avoid false negatives
                 */
                videoPacketsReceived++;
                for (const resume of resumeStatsUpdates) {
                    resume?.();
                }
                callSignals.onReconnected.emit();
            });
            callSignals.onReconnecting.emit();
            restartIce(getPeer());
        }
        videoPacketsReceived = update;
    };
};
/**
 * Creates a call that manages the peer connection lifecycle and delegates
 * call-type-specific behaviour to `strategy`.
 *
 * @example
 * ```ts
 * const call = createCall(options, createMCUStrategy(options));
 * ```
 */
export const createCall = (options, strategy, hooks) => createCallWithReceivers(options, strategy, hooks).call;
/**
 * Package-internal variant of `createCall` that also returns receiver
 * lookups by MID, separately from the `Call` itself.
 *
 * @example
 * ```ts
 * const {call, receivers} = createCallWithReceivers(
 *     options,
 *     createSFUStrategy(options),
 *     {onDisconnect: () => stopPolling()},
 * );
 * const track = receivers.getTrackByMid('3');
 * ```
 */
export const createCallWithReceivers = (options, strategy, { onDisconnect } = {}) => {
    const presoState = new Proxy({
        send: 'new',
        recv: 'new',
    }, {
        set: (target, p, value) => {
            const didUpdate = Reflect.set(target, p, value);
            if (didUpdate) {
                options.callSignals.onPresentationConnectionChange.emit(target);
            }
            return didUpdate;
        },
    });
    const pc = createPCCall({ callOptions: options, strategy });
    const disconnect = () => {
        onDisconnect?.();
        pc.cleanup();
        pc.close();
        presoState.recv = 'disconnected';
        presoState.send = 'disconnected';
    };
    const setStream = (stream) => {
        const isTarget = strategy.isLocalStreamTarget ?? isMainConfig;
        options.pcMainSignals.onOfferRequired.emit({
            stream,
            target: pc.peer
                .getTransceiverConfigs()
                .flatMap(config => (isTarget(config) ? [[config]] : [])),
            syncMedia: !pc.waitingForRemotePeer,
        });
    };
    const setBandwidth = (bandwidth) => {
        pc.peer.bandwidth = bandwidth;
    };
    const setDegradationPreference = (preference) => {
        for (const config of pc.peer.getTransceiverConfigs()) {
            if (isMainConfig(config) &&
                isVideoConfig(config) &&
                preference.video) {
                config.degradationPreference = preference.video;
            }
            else if (isPresoVideo(config) && preference.presentation) {
                config.degradationPreference = preference.presentation;
            }
        }
    };
    const present = async (stream) => {
        if (!stream) {
            return;
        }
        logger.debug({ presoState, stream }, 'present');
        try {
            presoState.send = 'connecting';
            presoState.recv = 'disconnected';
            const res = await options.takeFloor({});
            if (res?.status === 403) {
                throw new Error('Presentation rejected');
            }
            await pc.peer.setLocalStream(stream, pc.peer
                .getTransceiverConfigs()
                .flatMap(config => isPresoVideo(config) ? [[config, 'sendonly']] : []), !pc.waitingForRemotePeer);
            presoState.send = 'connected';
        }
        catch (error) {
            logger.warn({ error, presoState, stream }, 'failed to present');
            presoState.send = 'failed';
        }
    };
    const stopPresenting = async () => {
        logger.debug({ presoState }, 'stopPresenting');
        await options.releaseFloor({});
        await pc.peer.setLocalStream(undefined, pc.peer
            .getTransceiverConfigs()
            .flatMap(config => isPresoVideo(config) ? [[config, 'inactive']] : []), !pc.waitingForRemotePeer);
        presoState.send = 'disconnected';
    };
    const receivePresentation = async (event) => {
        if (['connecting', 'connected'].includes(presoState.recv)) {
            return;
        }
        logger.debug({ presoState }, 'receivePresentation');
        presoState.recv = 'connecting';
        const slidesConfigs = pc.peer
            .getTransceiverConfigs()
            .flatMap(config => (isPresoVideo(config) ? [config] : []));
        if (presoState.send === 'connected') {
            await pc.peer.setLocalStream(undefined, slidesConfigs.map(config => [config, 'inactive']), false);
            await options.releaseFloor({});
            presoState.send = 'disconnected';
        }
        if (presoState.recv === 'disconnected') {
            return;
        }
        if (isPresentationRevoked(event)) {
            presoState.recv = 'disconnected';
            return;
        }
        if (!options.isDirectMedia) {
            const [slidesConfig] = slidesConfigs;
            if (slidesConfig) {
                slidesConfig.direction = 'recvonly';
                slidesConfig.syncDirection();
            }
        }
        presoState.recv = 'connected';
    };
    const stopReceivingPresentation = () => {
        logger.debug({ presoState }, 'stopReceivingPresentation');
        if (presoState.recv === 'connected' ||
            presoState.recv === 'connecting') {
            presoState.recv = 'disconnected';
        }
        if (!options.isDirectMedia) {
            const [slidesConfig] = pc.peer
                .getTransceiverConfigs()
                .flatMap(config => (isPresoVideo(config) ? [config] : []));
            if (slidesConfig &&
                presoState.send !== 'connected' &&
                presoState.send !== 'connecting') {
                slidesConfig.direction = 'inactive';
                slidesConfig.syncDirection();
            }
        }
    };
    const sendDataChannelEvent = (event) => {
        if (pc.dataChannel?.readyState !== 'open') {
            logger.warn({ eventType: event.type }, 'DataChannel not open. Cannot send event.');
            return;
        }
        pc.dataChannel.send(JSON.stringify(event));
    };
    const findTransceiver = (mid) => pc.peer.peer.getTransceivers().find(t => t.mid === mid);
    const call = {
        get presoState() {
            return presoState;
        },
        get mediaStream() {
            const config = pc.peer.getTransceiverConfigs().find(config => {
                return isMainConfig(config) && config.streams[0];
            });
            return config?.streams[0];
        },
        get bandwidth() {
            return pc.peer.bandwidth;
        },
        disconnect,
        present,
        receivePresentation,
        setBandwidth,
        setDegradationPreference,
        setStream,
        stopPresenting,
        stopReceivingPresentation,
        sendDataChannelEvent,
    };
    return {
        call,
        receivers: {
            getTrackByMid: mid => findTransceiver(mid)?.receiver.track,
            getAudioLevelByMid: mid => findTransceiver(mid)?.receiver.getSynchronizationSources()[0]
                ?.audioLevel,
        },
    };
};
const createPCCall = ({ callOptions, strategy }) => {
    const { ack, sendOffer, update, newCandidate, peerOptions, signals, pcMainSignals, callSignals, isDirectMedia, mediaStream, timeToWaitForOfferInMS = 2000, } = callOptions;
    // -- Mutable props --
    const props = {
        called: false,
        /**
         * With direct media, we should not sync the transceivers until the remote peer is present.
         * This means having set the remote description to an offer and the local to an answer.
         */
        waitingForRemotePeer: false,
        updateController: undefined,
        offerIgnoredTimeout: 0,
        turnRestartTimeout: 0,
        apiOneTimeCallbacks: {},
    };
    const clearOfferIgnoredTimeout = () => {
        clearTimeout(props.offerIgnoredTimeout);
        props.offerIgnoredTimeout = 0;
    };
    const clearTurnRestartTimeout = () => {
        clearTimeout(props.turnRestartTimeout);
        props.turnRestartTimeout = 0;
    };
    const createPC = strategy.createPC;
    // -- ICE queues --
    const incomingICECandidateQueue = createEventQueue(pcMainSignals.onReceiveIceCandidate.emit, false);
    const executeOneTimeApiCallback = (func) => {
        const callbacks = props.apiOneTimeCallbacks[func];
        if (callbacks) {
            for (const cb of callbacks) {
                cb();
            }
            props.apiOneTimeCallbacks[func] = undefined;
        }
    };
    const sendCandidate = (candidate) => {
        queue.enqueue(async () => {
            await newCandidate({ candidate });
            executeOneTimeApiCallback('newCandidate');
        });
    };
    const outgoingICECandidateQueue = createEventQueue(sendCandidate, false);
    const addOnceApiCallback = (func, cb) => {
        const cbArray = props.apiOneTimeCallbacks[func];
        if (cbArray) {
            cbArray.push(cb);
        }
        else {
            props.apiOneTimeCallbacks[func] = [cb];
        }
    };
    // -- Signaling queue --
    const queue = createAsyncQueue({
        throttleInMS: 0,
        delayInMS: 0,
        size: 100,
        timeoutInMS: 15000,
        handleTimeout: async () => {
            logger.info({
                updateController: props.updateController,
                isDirectMedia,
                called: props.called,
            }, 'Signaling request timeout');
            // FIXME: a proper way to handle request timeout
            if (isDirectMedia) {
                logger.info({
                    updateController: props.updateController,
                    isDirectMedia,
                    called: props.called,
                }, 'Restarting Peer Connection for direct media due to signaling timeout');
                props.updateController?.abort();
                queue.empty();
                incomingICECandidateQueue.discard();
                outgoingICECandidateQueue.discard();
                peer.close();
                peer = createPC(peerOptions.rtcConfig, true);
                props.waitingForRemotePeer = true;
            }
        },
    });
    // -- Stats (delegated to strategy) --
    const setupStats = () => strategy.setupStats(peer);
    const cleanupStats = () => strategy.cleanupStats();
    // -- ICE helpers --
    const restartIce = (targetPeer) => {
        if (!['new', 'closed'].includes(targetPeer.connectionState)) {
            incomingICECandidateQueue.discard();
            outgoingICECandidateQueue.discard();
            targetPeer.restartIce();
        }
    };
    let peer = createPC();
    const addIceServers = (iceServers) => {
        const rtcConfig = peer.getConfiguration();
        return isEmpty(iceServers)
            ? rtcConfig
            : {
                ...rtcConfig,
                iceServers: [...(rtcConfig.iceServers ?? []), ...iceServers],
            };
    };
    const isIceConnecting = (iceConnectionState) => ['new', 'checking'].includes(iceConnectionState);
    /**
     * Restarts ICE to gather relay candidates only when ICE has not connected
     * without them in time. An ICE session that connects keeps its path, as a
     * restart during its DTLS handshake disrupts media. Disconnected and failed
     * sessions are restarted by the peer connection's own ICE recovery, which
     * uses the updated configuration.
     */
    const scheduleTurnRestart = () => {
        clearTurnRestartTimeout();
        if (!isIceConnecting(peer.iceConnectionState)) {
            return;
        }
        const targetPeer = peer;
        props.turnRestartTimeout = window.setTimeout(() => {
            props.turnRestartTimeout = 0;
            if (targetPeer === peer &&
                isIceConnecting(targetPeer.iceConnectionState)) {
                logger.info('Restarting ICE with TURN servers as ICE has not connected');
                targetPeer.restartIce();
            }
        }, TURN_ICE_RESTART_DELAY_MS);
    };
    const handleIceConnectionStateChange = (iceConnectionState) => {
        // ICE that connects, or fails and is recovered, needs no TURN restart
        if (!isIceConnecting(iceConnectionState)) {
            clearTurnRestartTimeout();
        }
    };
    const handleAck = (turn) => {
        if (!turn) {
            return;
        }
        const rtcConfig = addIceServers(turn);
        if (peer.setConfiguration) {
            peer.setConfiguration(rtcConfig);
            scheduleTurnRestart();
        }
        else {
            peer.close();
            peer = createPC(rtcConfig);
            pcMainSignals.onOfferRequired.emit(mediaStream && {
                stream: mediaStream,
                target: peer
                    .getTransceiverConfigs()
                    .flatMap(config => isMainConfig(config) ? [[config]] : []),
            });
        }
    };
    // -- Packet stagnation --
    const handlePacketStagnation = createPacketStagnationHandler({
        isDirectMedia,
        callSignals,
        addOnceApiCallback,
        restartIce,
        getPeer: () => peer,
        resetStats: () => strategy.resetStats(),
    });
    // -- API signal handlers --
    const handleAnswer = ({ sdp, turn, offer_ignored, }) => {
        if (isDirectMedia &&
            (offer_ignored || !sdp) &&
            peer.connectionState !== 'connected') {
            // Discard the call and wait for `new_offer` for direct media
            // Assign polite peer role to the new Peer Connection instance
            logger.info({ sdp, turn, offer_ignored, called: props.called }, 'Offer being ignored');
            queue.empty();
            const restartPC = () => {
                logger.info({ sdp, turn, offer_ignored, called: props.called }, 'Restarting Peer Connection');
                peer.close();
                peer = createPC(peerOptions.rtcConfig, true);
                props.waitingForRemotePeer = true;
                props.offerIgnoredTimeout = 0;
            };
            if (props.called) {
                props.offerIgnoredTimeout = window.setTimeout(restartPC, timeToWaitForOfferInMS);
            }
            else {
                restartPC();
            }
            return;
        }
        if (offer_ignored || !sdp) {
            return;
        }
        const answer = new RTCSessionDescription({ sdp, type: 'answer' });
        const turnServers = isEmpty(turn) ? undefined : turn;
        queue.enqueue(async () => {
            await ack({});
            handleAck(turnServers);
        });
        pcMainSignals.onReceiveAnswer.emit(answer);
    };
    const handleNewOffer = (sdp) => {
        if (!sdp) {
            return;
        }
        pcMainSignals.onReceiveOffer.emit(new RTCSessionDescription({ sdp, type: 'offer' }));
    };
    const handleUpdateSdp = (sdp) => {
        if (!sdp) {
            return;
        }
        if (props.offerIgnoredTimeout) {
            clearOfferIgnoredTimeout();
        }
        pcMainSignals.onReceiveOffer.emit(new RTCSessionDescription({ sdp, type: 'offer' }));
    };
    const handleIncomingIceCandidate = (_candidate) => {
        let candidate = _candidate;
        // Strip unnecessary properties to please Safari when end-of-candidates.
        if (candidate.candidate === '') {
            // Manually indicated end-of-candidates
            candidate = { candidate: '' };
        }
        incomingICECandidateQueue.enqueue(candidate);
    };
    // -- PC signal handlers --
    const handleLocalOffer = ({ sdp }) => {
        if (!sdp) {
            return;
        }
        queue.enqueue(async () => {
            if (props.called) {
                props.updateController = new AbortController();
                await update({
                    sdp,
                    abortSignal: props.updateController.signal,
                });
                props.updateController = undefined;
            }
            else {
                await sendOffer({ sdp });
                props.called = true;
            }
        });
    };
    const handleLocalAnswer = ({ sdp }) => {
        props.waitingForRemotePeer = false;
        props.updateController?.abort(); // Abort current update call
        props.called = true;
        queue.enqueue(async () => {
            await ack({ sdp });
            handleAck();
        });
    };
    const handleRemoteStreams = (transceiverConfig) => {
        strategy.handleRemoteStreams(transceiverConfig, peer);
    };
    const handleOutgoingIceCandidate = (event) => {
        const candidate = toIceCandidate(event);
        outgoingICECandidateQueue.enqueue(candidate);
    };
    const handleConnectionStateChange = (connectionState) => {
        switch (connectionState) {
            case 'connected': {
                callSignals.onCallConnected.emit();
                break;
            }
            case 'closed': {
                signals.onError.emit({
                    error: 'WebRTC connection closed',
                    errorCode: '#pex117',
                });
                break;
            }
            case 'failed': {
                signals.onError.emit({
                    error: 'WebRTC connection failed',
                    errorCode: '#pex128',
                });
                break;
            }
            default:
                break;
        }
    };
    const handleSignalingStateChange = (state) => {
        if (state === 'closed') {
            signals.onError.emit({
                error: 'WebRTC connection closed',
                errorCode: '#pex117',
            });
        }
    };
    // -- Wire up signals --
    let detachApiSignals = [
        signals.onAnswer.add(handleAnswer),
        signals.onNewOffer.add(handleNewOffer),
        signals.onUpdateSdp.add(handleUpdateSdp),
        signals.onIceCandidate.add(handleIncomingIceCandidate),
    ];
    let detachStatsSignals = strategy.wireStatsSignals(peer, handlePacketStagnation);
    let detachPCSignals = [
        pcMainSignals.onOffer.add(handleLocalOffer),
        pcMainSignals.onOfferIgnored.add(() => {
            void ack({ offerIgnored: true });
        }),
        pcMainSignals.onAnswer.add(handleLocalAnswer),
        pcMainSignals.onRemoteStreams.add(handleRemoteStreams),
        pcMainSignals.onIceCandidate.add(handleOutgoingIceCandidate),
        pcMainSignals.onConnectionStateChange.add(handleConnectionStateChange),
        pcMainSignals.onIceConnectionStateChange.add(handleIceConnectionStateChange),
        pcMainSignals.onSignalingStateChange.add(handleSignalingStateChange),
        pcMainSignals.onError.add(error => {
            // FIXME: we need to handle the error properly
            logger.error(error.context, error.message);
        }),
        pcMainSignals.onTransceiverChange.add(() => {
            cleanupStats();
            setupStats();
        }),
        pcMainSignals.onSecureCheckCode.add(callSignals.onSecureCheckCode.emit),
    ];
    // -- Lifecycle --
    const cleanup = () => {
        for (const detach of detachApiSignals) {
            detach();
        }
        detachApiSignals = [];
        for (const detach of detachPCSignals) {
            detach();
        }
        detachPCSignals = [];
        for (const detach of detachStatsSignals) {
            detach();
        }
        detachStatsSignals = [];
        cleanupStats();
        props.apiOneTimeCallbacks = {};
    };
    const close = () => {
        props.called = false;
        props.waitingForRemotePeer = false;
        incomingICECandidateQueue.discard();
        outgoingICECandidateQueue.discard();
        queue.empty();
        clearOfferIgnoredTimeout();
        clearTurnRestartTimeout();
        if (peer.connectionState === 'closed') {
            return;
        }
        peer.close();
    };
    // Start negotiation after successfully subscribing to all signals
    pcMainSignals.onOfferRequired.emit({
        syncMedia: !props.waitingForRemotePeer,
    });
    return {
        get peer() {
            return peer;
        },
        get dataChannel() {
            const [config] = peer.getDataChannelConfigs();
            return config?.dataChannel;
        },
        get waitingForRemotePeer() {
            return props.waitingForRemotePeer;
        },
        cleanup,
        close,
    };
};
//# sourceMappingURL=call.js.map